/**
 * Service WebRTC Universel pour Appels Vocaux AstroX (Client <-> Conseiller)
 * Architecture Multi-Lignes avec Isolation Audio, Persistance F5 et Reattachement
 * Supporte : 1-8 lignes simultanees par agent, Hold/Mute/Transfer par ligne,
 * File d'Attente ACD, Repondeur Automatique, et Reconnexion apres rechargement
 */

import { socket } from './socket';

export interface VoiceCallSession {
    callId: string;
    mediumId: string;
    mediumName: string;
    mediumImage?: string;
    clientId: string;
    clientName: string;
    clientPhoto?: string;
    clientSign?: string;
    clientPoints?: number;
    role: "caller" | "receiver";
    status: "idle" | "queued" | "calling" | "incoming" | "connected" | "ended" | "rejected" | "busy" | "autoanswer";
    startTime?: number;
    durationSeconds: number;
    isMuted: boolean;
    queuePosition?: number;
    queueLength?: number;
    estimatedWaitMinutes?: number;
    isOnHold?: boolean;
    isTransferring?: boolean;
    transferMessage?: string;
    agentUsername?: string;
    autoplayBlocked?: boolean;
}

export interface QueuedCallInfo {
    callId: string;
    position: number;
    clientId: string;
    clientName: string;
    clientPhoto?: string;
    clientSign?: string;
    clientPoints?: number;
    mediumId: string;
    mediumName: string;
    priority: number;
    targetAgentUsername?: string;
    waitDurationSeconds: number;
}

export interface CallLine {
    callId: string;
    peerConnection: RTCPeerConnection | null;
    remoteStream: MediaStream | null;
    remoteAudioElement: HTMLAudioElement;
    mohAudioElement: HTMLAudioElement | null;
    localAudioTrack: MediaStreamTrack | null;
    gainNode: GainNode | null;
    session: VoiceCallSession;
    iceCandidatesQueue: RTCIceCandidateInit[];
    isRemoteDescriptionSet: boolean;
    durationTimer: any;
    analyser: AnalyserNode | null;
    animationFrameId: number | null;
    networkQuality: 'excellent' | 'good' | 'poor' | 'unknown';
    statsInterval: any;
}

const ICE_SERVERS: RTCConfiguration = {
    iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "stun:stun2.l.google.com:19302" },
        { urls: "stun:stun3.l.google.com:19302" },
        { urls: "stun:stun4.l.google.com:19302" },
        { urls: "stun:stun.cloudflare.com:3478" }
    ],
    iceCandidatePoolSize: 10
};

const MAX_LINES_PER_AGENT = 10;
const SESSION_STORAGE_KEY = 'astrox_active_calls';
const MOH_FADE_DURATION_MS = 500;
const NETWORK_STATS_CHECK_INTERVAL_MS = 5000;
const ICE_RESTART_PACKET_LOSS_THRESHOLD = 0.15; // 15% packet loss

class WebRtcVoiceService {
    private lines: Map<string, CallLine> = new Map();
    private activeLineId: string | null = null;
    private sharedLocalStream: MediaStream | null = null;
    private currentSession: VoiceCallSession | null = null;
    private audioContext: AudioContext | null = null;
    private ringtoneInterval: any = null;
    private sessionListeners: Set<(session: VoiceCallSession | null) => void> = new Set();
    private streamListeners: Set<(stream: MediaStream | null) => void> = new Set();
    private audioLevelListeners: Set<(level: number) => void> = new Set();
    private queueListeners: Set<(queue: QueuedCallInfo[]) => void> = new Set();
    private linesListeners: Set<(lines: CallLine[]) => void> = new Set();

    constructor() {
        if (typeof window !== "undefined") {
            this.setupSocketListeners();
            this.attemptReattach();
        }
    }

    private createAudioElement(callId: string): HTMLAudioElement {
        if (typeof document === "undefined") return null as any;
        const elemId = `astrox-remote-audio-${callId}`;
        let audio = document.getElementById(elemId) as HTMLAudioElement;
        if (!audio) {
            audio = document.createElement("audio");
            audio.id = elemId;
            audio.autoplay = true;
            audio.muted = false;
            audio.volume = 1.0;
            (audio as any).playsInline = true;
            audio.style.cssText = "position:fixed;bottom:0;right:0;width:1px;height:1px;opacity:0.01;pointer-events:none;z-index:999999";
            document.body.appendChild(audio);
        }
        return audio;
    }

    private ensureAudioElement(): HTMLAudioElement | null {
        if (this.activeLineId) {
            const line = this.lines.get(this.activeLineId);
            if (line) return line.remoteAudioElement;
        }
        if (typeof document === "undefined") return null;
        let audio = document.getElementById("astrox-remote-audio") as HTMLAudioElement;
        if (!audio) {
            audio = document.createElement("audio");
            audio.id = "astrox-remote-audio";
            audio.autoplay = true;
            audio.muted = false;
            audio.volume = 1.0;
            (audio as any).playsInline = true;
            audio.style.cssText = "position:fixed;bottom:0;right:0;width:1px;height:1px;opacity:0.01;pointer-events:none;z-index:999999";
            document.body.appendChild(audio);
        }
        return audio;
    }

    private getOrCreateAudioContext(): AudioContext | null {
        try {
            if (!this.audioContext || this.audioContext.state === "closed") {
                const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
                if (AudioCtx) this.audioContext = new AudioCtx({ sampleRate: 48000 });
            }
            if (this.audioContext && this.audioContext.state === "suspended") {
                this.audioContext.resume().catch(() => {});
            }
            return this.audioContext;
        } catch (e) { return null; }
    }

    public unlockAudioPlayback() {
        try {
            for (const line of this.lines.values()) {
                if (line.remoteAudioElement) {
                    line.remoteAudioElement.muted = false;
                    line.remoteAudioElement.volume = line.session.isOnHold ? 0 : 1.0;
                    line.remoteAudioElement.play().catch(() => {});
                }
            }
            const fallback = this.ensureAudioElement();
            if (fallback) { fallback.muted = false; fallback.volume = 1.0; fallback.play().catch(() => {}); }
            const ctx = this.getOrCreateAudioContext();
            if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
        } catch (e) {}
    }

    private updateSession(updates: Partial<VoiceCallSession> | null) {
        if (updates === null) { this.currentSession = null; }
        else if (this.currentSession) { this.currentSession = { ...this.currentSession, ...updates }; }
        this.sessionListeners.forEach(cb => cb(this.currentSession ? { ...this.currentSession } : null));
    }

    private updateLineSession(callId: string, updates: Partial<VoiceCallSession>) {
        const line = this.lines.get(callId);
        if (line) {
            line.session = { ...line.session, ...updates };
            if (callId === this.activeLineId) {
                this.currentSession = { ...line.session };
                this.sessionListeners.forEach(cb => cb(this.currentSession ? { ...this.currentSession } : null));
            }
            this.notifyLinesUpdate();
            this.persistSessionState();
        }
    }

    private notifyLinesUpdate() {
        const arr = Array.from(this.lines.values());
        this.linesListeners.forEach(cb => cb(arr));
    }

    private persistSessionState() {
        try {
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
                lines: Array.from(this.lines.entries()).map(([id, l]) => ({ callId: id, session: l.session })),
                activeLineId: this.activeLineId,
                timestamp: Date.now()
            }));
        } catch (e) {}
    }

    private loadPersistedState(): any | null {
        try {
            const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (!raw) return null;
            const data = JSON.parse(raw);
            if (data.timestamp && (Date.now() - data.timestamp) > 20000) { sessionStorage.removeItem(SESSION_STORAGE_KEY); return null; }
            return data;
        } catch (e) { return null; }
    }

    private clearPersistedState() { try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch (e) {} }

    private attemptReattach() {
        const persisted = this.loadPersistedState();
        if (!persisted || !persisted.lines || persisted.lines.length === 0) return;
        console.log('[WebRTC] Found persisted calls, attempting reattach...');
        const doReattach = () => {
            const callIds = persisted.lines.map((l: any) => l.callId);
            const username = persisted.lines[0]?.session?.agentUsername;
            if (!username) { this.clearPersistedState(); return; }
            socket.emit('voice_call_reattach', { username, callIds });
        };
        if (socket.connected) setTimeout(doReattach, 500);
        else socket.once('connect', () => setTimeout(doReattach, 500));
    }

    private setupSocketListeners() {
        socket.on("voice_call_queued", (data: { callId: string; position: number; queueLength: number; estimatedWaitMinutes: number }) => {
            if (this.currentSession && this.currentSession.callId === data.callId) {
                this.stopRingtone();
                this.updateSession({ status: "queued", queuePosition: data.position, queueLength: data.queueLength, estimatedWaitMinutes: data.estimatedWaitMinutes });
            }
        });

        socket.on("voice_call_queue_update", (data: { callId: string; position: number; queueLength: number; estimatedWaitMinutes: number }) => {
            if (this.currentSession && this.currentSession.callId === data.callId) {
                this.updateSession({ queuePosition: data.position, queueLength: data.queueLength, estimatedWaitMinutes: data.estimatedWaitMinutes });
            }
        });

        socket.on("voice_call_ringing_agent", (data: { callId: string; agentUsername?: string }) => {
            if (this.currentSession && this.currentSession.callId === data.callId) {
                this.updateSession({ status: "calling", agentUsername: data.agentUsername });
                this.playRingtone(false);
            }
        });

        socket.on("voice_call_incoming", (data: any) => {
            if (this.currentSession && this.currentSession.role === "caller") return;
            const activeLines = Array.from(this.lines.values()).filter(l => l.session.status === 'connected' || l.session.status === 'incoming');
            if (activeLines.length >= MAX_LINES_PER_AGENT) { socket.emit("voice_call_busy", { callId: data.callId }); return; }
            if (this.lines.has(data.callId)) return;

            const newSession: VoiceCallSession = {
                callId: data.callId, mediumId: String(data.mediumId), mediumName: data.mediumName, mediumImage: data.mediumImage,
                clientId: String(data.clientId), clientName: data.clientName, clientPhoto: data.clientPhoto,
                clientSign: data.clientSign || "Astrologie", clientPoints: Number(data.clientPoints) || 100,
                role: "receiver", status: "incoming", durationSeconds: 0, isMuted: false,
                isTransferring: Boolean(data.isTransfer),
                transferMessage: data.transferredFrom ? `Transfert de ${data.transferredFrom}` : undefined
            };

            const audioElem = this.createAudioElement(data.callId);
            this.lines.set(data.callId, {
                callId: data.callId, peerConnection: null, remoteStream: null, remoteAudioElement: audioElem,
                session: newSession, iceCandidatesQueue: [], isRemoteDescriptionSet: false,
                durationTimer: null, analyser: null, animationFrameId: null
            });

            if (!this.activeLineId || !this.lines.has(this.activeLineId)) {
                this.activeLineId = data.callId;
                this.currentSession = { ...newSession };
            }
            this.sessionListeners.forEach(cb => cb(this.currentSession ? { ...this.currentSession } : null));
            this.notifyLinesUpdate();
            this.persistSessionState();
            this.playRingtone(true);
        });

        socket.on("voice_call_accepted", async (data: { callId: string; agentUsername?: string }) => {
            const line = this.lines.get(data.callId);
            if (line) {
                this.stopRingtone(); this.unlockAudioPlayback();
                this.updateLineSession(data.callId, { status: "connected", startTime: Date.now(), agentUsername: data.agentUsername || line.session.agentUsername });
                this.startLineDurationTimer(data.callId);
                if (line.session.role === "caller") await this.createAndSendOfferForLine(data.callId);
            } else if (this.currentSession && this.currentSession.callId === data.callId) {
                this.stopRingtone(); this.unlockAudioPlayback();
                this.updateSession({ status: "connected", startTime: Date.now(), agentUsername: data.agentUsername || this.currentSession.agentUsername });
                this.startDurationTimer();
                if (this.currentSession.role === "caller") await this.createAndSendOffer();
            }
        });

        socket.on("voice_call_transferred_away", (data: { callId: string }) => {
            if (this.lines.has(data.callId)) { this.cleanupLine(data.callId); }
            else if (this.currentSession && this.currentSession.callId === data.callId) { this.cleanup(); this.currentSession = null; this.sessionListeners.forEach(cb => cb(null)); }
        });

        socket.on("voice_call_signal", async (data: { callId: string; signal: any }) => {
            const line = this.lines.get(data.callId);
            const isCurrent = this.currentSession && this.currentSession.callId === data.callId;
            if (!line && !isCurrent) return;
            try {
                if (data.signal && data.signal.type === "offer") {
                    if (line) await this.handleOfferForLine(data.callId, data.signal);
                    else await this.handleOffer(data.signal);
                } else if (data.signal && data.signal.type === "answer") {
                    if (line) await this.handleAnswerForLine(data.callId, data.signal);
                    else await this.handleAnswer(data.signal);
                } else if (data.signal && data.signal.candidate) {
                    if (line) await this.handleIceCandidateForLine(data.callId, data.signal.candidate);
                    else await this.handleIceCandidate(data.signal.candidate);
                }
            } catch (err) { console.error("[WebRTC] Signal error:", err); }
        });

        socket.on("voice_call_hold_state", (data: { callId: string; isOnHold: boolean; isTransferring?: boolean; message?: string }) => {
            const line = this.lines.get(data.callId);
            if (line) {
                this.updateLineSession(data.callId, { isOnHold: data.isOnHold, isTransferring: Boolean(data.isTransferring), transferMessage: data.message });
                line.remoteAudioElement.volume = data.isOnHold ? 0 : 1.0;
            } else if (this.currentSession && this.currentSession.callId === data.callId) {
                this.updateSession({ isOnHold: data.isOnHold, isTransferring: Boolean(data.isTransferring), transferMessage: data.message });
            }
        });

        socket.on("call_queue_updated", (data: { queueLength: number; queue: QueuedCallInfo[] }) => {
            this.queueListeners.forEach(cb => cb(data.queue || []));
        });

        socket.on("voice_call_rejected", (data: { callId: string }) => {
            if (this.lines.has(data.callId)) { this.stopRingtone(); this.updateLineSession(data.callId, { status: "rejected" }); this.cleanupLine(data.callId); }
            else if (this.currentSession && this.currentSession.callId === data.callId) { this.stopRingtone(); this.updateSession({ status: "rejected" }); this.cleanup(); }
        });

        socket.on("voice_call_busy", (data: { callId: string }) => {
            if (this.lines.has(data.callId)) { this.stopRingtone(); this.updateLineSession(data.callId, { status: "busy" }); this.cleanupLine(data.callId); }
            else if (this.currentSession && this.currentSession.callId === data.callId) { this.stopRingtone(); this.updateSession({ status: "busy" }); this.cleanup(); }
        });

        socket.on("voice_call_ended", (data: { callId: string }) => {
            if (this.lines.has(data.callId)) { this.stopRingtone(); this.updateLineSession(data.callId, { status: "ended" }); this.cleanupLine(data.callId); }
            else if (this.currentSession && this.currentSession.callId === data.callId) { this.stopRingtone(); this.updateSession({ status: "ended" }); this.cleanup(); }
        });

        socket.on("voice_call_answered_elsewhere", (data: { callId: string }) => {
            const line = this.lines.get(data.callId);
            // CRITIQUE : Seuls les agents (receivers) qui ont une sonnerie entrante (incoming) doivent l'effacer.
            // L'appelant (caller / client) ne doit JAMAIS fermer son appel sur answered_elsewhere !
            if (line && line.session.role === "receiver" && line.session.status === "incoming") {
                this.stopRingtone();
                this.updateLineSession(data.callId, { status: "ended" });
                this.cleanupLine(data.callId);
            } else if (this.currentSession && this.currentSession.callId === data.callId && this.currentSession.role === "receiver" && this.currentSession.status === "incoming") {
                this.stopRingtone();
                this.updateSession({ status: "ended" });
                this.cleanup();
            }
        });

        socket.on("voice_call_transfer_snapback", (data: any) => {
            if (this.lines.has(data.callId)) {
                this.updateLineSession(data.callId, { isTransferring: false, transferMessage: 'Transfert echoue — appel de retour' });
            } else {
                const newSession: VoiceCallSession = {
                    callId: data.callId, mediumId: String(data.mediumId || 'unknown'), mediumName: data.mediumName || 'Unknown', mediumImage: data.mediumImage,
                    clientId: String(data.clientId || 'unknown'), clientName: data.clientName || 'Unknown', clientPhoto: data.clientPhoto,
                    clientSign: data.clientSign || "Astrologie", clientPoints: Number(data.clientPoints) || 100,
                    role: "receiver", status: "incoming", durationSeconds: 0, isMuted: false,
                    isTransferring: false,
                    transferMessage: 'Transfert echoue — appel de retour'
                };

                const audioElem = this.createAudioElement(data.callId);
                this.lines.set(data.callId, {
                    callId: data.callId, peerConnection: null, remoteStream: null, remoteAudioElement: audioElem,
                    session: newSession, iceCandidatesQueue: [], isRemoteDescriptionSet: false,
                    durationTimer: null, analyser: null, animationFrameId: null
                });

                if (!this.activeLineId || !this.lines.has(this.activeLineId)) {
                    this.activeLineId = data.callId;
                    this.currentSession = { ...newSession };
                }
                this.sessionListeners.forEach(cb => cb(this.currentSession ? { ...this.currentSession } : null));
                this.notifyLinesUpdate();
                this.persistSessionState();
                this.playRingtone(true);
            }
        });

        socket.on("voice_call_timeout_voicemail", (data: { callId: string }) => {
            if (this.currentSession && this.currentSession.callId === data.callId) {
                this.updateSession({ status: "autoanswer" });
            }
        });

        socket.on("voice_call_autoanswer_start", (data: { callId: string }) => {
            if (this.currentSession && this.currentSession.callId === data.callId) this.updateSession({ status: "autoanswer" });
        });

        socket.on("voice_call_reattach_success", async (data: { calls: any[] }) => {
            for (const c of data.calls) {
                const session: VoiceCallSession = {
                    callId: c.callId, mediumId: c.callData?.mediumId || 'sarah', mediumName: c.callData?.mediumName || 'Sarah Astrologue',
                    mediumImage: c.callData?.mediumImage, clientId: c.callData?.clientId || 'client', clientName: c.callData?.clientName || 'Client AstroX',
                    clientPhoto: c.callData?.clientPhoto, clientSign: c.callData?.clientSign || 'Astrologie', clientPoints: c.callData?.clientPoints || 100,
                    role: "receiver", status: "connected", startTime: Date.now() - ((c.durationSeconds || 0) * 1000),
                    durationSeconds: c.durationSeconds || 0, isMuted: false, agentUsername: c.callData?.agentUsername
                };
                const audioElem = this.createAudioElement(c.callId);
                this.lines.set(c.callId, { callId: c.callId, peerConnection: null, remoteStream: null, remoteAudioElement: audioElem, session, iceCandidatesQueue: [], isRemoteDescriptionSet: false, durationTimer: null, analyser: null, animationFrameId: null });
                if (!this.sharedLocalStream) {
                    try { this.sharedLocalStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false }); } catch (e) {}
                }
                if (this.sharedLocalStream) { this.initPeerConnectionForLine(c.callId); await this.createAndSendOfferForLine(c.callId); }
                this.startLineDurationTimer(c.callId);
            }
            if (data.calls.length > 0 && !this.activeLineId) this.activeLineId = data.calls[0].callId;
            this.syncCurrentSessionFromActiveLine();
            this.notifyLinesUpdate();
            this.persistSessionState();
        });

        socket.on("voice_call_reattach_failed", () => { this.clearPersistedState(); });

        socket.on("voice_call_ice_restart", async (data: { callId: string }) => {
            const line = this.lines.get(data.callId);
            if (line?.peerConnection) {
                try {
                    const offer = await line.peerConnection.createOffer({ iceRestart: true });
                    await line.peerConnection.setLocalDescription(offer);
                    socket.emit("voice_call_signal", { callId: data.callId, signal: { type: offer.type, sdp: offer.sdp } });
                } catch (e) {}
            }
        });
    }

    public async startCall(mediumId: string, mediumName: string, mediumImage: string, clientId: string, clientName: string, clientSign?: string, clientPoints?: number): Promise<boolean> {
        try {
            this.cleanup(); this.unlockAudioPlayback();
            this.sharedLocalStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
            const callId = `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
            this.currentSession = { callId, mediumId: String(mediumId), mediumName, mediumImage, clientId: String(clientId), clientName, clientSign: clientSign || "Astrologie", clientPoints: Number(clientPoints) || 100, role: "caller", status: "calling", durationSeconds: 0, isMuted: false };
            const audioElem = this.createAudioElement(callId);
            this.lines.set(callId, { callId, peerConnection: null, remoteStream: null, remoteAudioElement: audioElem, session: { ...this.currentSession }, iceCandidatesQueue: [], isRemoteDescriptionSet: false, durationTimer: null, analyser: null, animationFrameId: null });
            this.activeLineId = callId;
            this.sessionListeners.forEach(cb => cb(this.currentSession ? { ...this.currentSession } : null));
            this.notifyLinesUpdate();
            this.playRingtone(false);
            this.initPeerConnectionForLine(callId);
            this.setupAudioAnalyser(this.sharedLocalStream);
            socket.emit("voice_call_initiate", { callId, mediumId: String(mediumId), mediumName, mediumImage, clientId: String(clientId), clientName, clientSign: clientSign || "Astrologie", clientPoints: Number(clientPoints) || 100 });
            this.persistSessionState();
            return true;
        } catch (err: any) { console.error("[WebRTC] Mic error:", err); this.cleanup(); return false; }
    }

    public async acceptCall(): Promise<boolean> {
        const callId = this.activeLineId;
        if (!callId) return false;
        return this.acceptCallById(callId);
    }

    public async acceptCallById(callId: string): Promise<boolean> {
        const line = this.lines.get(callId);
        if (!line) {
            if (!this.currentSession) return false;
            try {
                this.stopRingtone(); this.unlockAudioPlayback();
                this.sharedLocalStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
                this.setupAudioAnalyser(this.sharedLocalStream);
                this.initPeerConnection();
                socket.emit("voice_call_accept", { callId: this.currentSession.callId });
                this.updateSession({ status: "connected", startTime: Date.now() });
                this.startDurationTimer();
                return true;
            } catch (err) { this.rejectCall(); return false; }
        }
        try {
            this.stopRingtone(); this.unlockAudioPlayback();
            if (!this.sharedLocalStream) {
                this.sharedLocalStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
                this.setupAudioAnalyser(this.sharedLocalStream);
            }
            if (this.activeLineId && this.activeLineId !== callId) this.holdLineInternal(this.activeLineId, true);
            this.activeLineId = callId;
            this.initPeerConnectionForLine(callId);
            socket.emit("voice_call_accept", { callId });
            this.updateLineSession(callId, { status: "connected", startTime: Date.now() });
            this.startLineDurationTimer(callId);
            this.syncCurrentSessionFromActiveLine();
            this.persistSessionState();
            return true;
        } catch (err) { this.rejectCallById(callId); return false; }
    }

    public rejectCall() {
        if (this.activeLineId && this.lines.has(this.activeLineId)) this.rejectCallById(this.activeLineId);
        else if (this.currentSession) { this.stopRingtone(); socket.emit("voice_call_reject", { callId: this.currentSession.callId }); this.updateSession({ status: "rejected" }); this.cleanup(); }
    }

    public rejectCallById(callId: string) {
        this.stopRingtone(); socket.emit("voice_call_reject", { callId });
        if (this.lines.has(callId)) { this.updateLineSession(callId, { status: "rejected" }); this.cleanupLine(callId); }
    }

    public endCall() {
        if (this.activeLineId && this.lines.has(this.activeLineId)) {
            const line = this.lines.get(this.activeLineId)!;
            this.stopRingtone();
            socket.emit("voice_call_end", { callId: this.activeLineId, durationSeconds: line.session.durationSeconds });
            this.updateLineSession(this.activeLineId, { status: "ended" });
            this.cleanupLine(this.activeLineId);
        } else if (this.currentSession) {
            this.stopRingtone();
            socket.emit("voice_call_end", { callId: this.currentSession.callId, durationSeconds: this.currentSession.durationSeconds });
            this.updateSession({ status: "ended" }); this.cleanup();
        }
    }

    public resetCall() { this.cleanup(); this.currentSession = null; this.sessionListeners.forEach(cb => cb(null)); }

    public toggleMute(): boolean {
        if (!this.sharedLocalStream) return false;
        const track = this.sharedLocalStream.getAudioTracks()[0];
        if (track) {
            track.enabled = !track.enabled;
            const isMuted = !track.enabled;
            if (this.activeLineId) this.updateLineSession(this.activeLineId, { isMuted });
            this.updateSession({ isMuted });
            return isMuted;
        }
        return false;
    }

    public holdCall(isHold: boolean) {
        if (this.activeLineId) { socket.emit("voice_call_hold", { callId: this.activeLineId, isHold }); this.holdLineInternal(this.activeLineId, isHold); }
        else if (this.currentSession) socket.emit("voice_call_hold", { callId: this.currentSession.callId, isHold });
    }

    public transferCall(targetUsername: string) {
        const callId = this.activeLineId || this.currentSession?.callId;
        if (!callId) return;
        socket.emit("voice_call_transfer", { callId, targetUsername });
    }

    public requestCallback(clientPhone: string, notes?: string) {
        const callId = this.currentSession?.callId;
        if (!callId || !clientPhone) return;
        socket.emit("voice_call_request_callback", { callId, clientPhone, notes });
    }

    public switchLine(callId: string) {
        if (!this.lines.has(callId) || callId === this.activeLineId) return;
        if (this.activeLineId && this.lines.has(this.activeLineId)) {
            const cur = this.lines.get(this.activeLineId)!;
            if (cur.session.status === 'connected') { this.holdLineInternal(this.activeLineId, true); socket.emit("voice_call_hold", { callId: this.activeLineId, isHold: true }); }
        }
        this.activeLineId = callId;
        const target = this.lines.get(callId)!;
        if (target.session.isOnHold && target.session.status === 'connected') { this.holdLineInternal(callId, false); socket.emit("voice_call_hold", { callId, isHold: false }); }
        this.syncCurrentSessionFromActiveLine();
        this.notifyLinesUpdate();
        this.persistSessionState();
    }

    public getLines(): CallLine[] { return Array.from(this.lines.values()); }
    public getActiveLine(): CallLine | null { return this.activeLineId ? this.lines.get(this.activeLineId) || null : null; }
    public getActiveLineId(): string | null { return this.activeLineId; }

    public prioritizeQueuedCall(callId: string) { socket.emit("admin_queue_prioritize", { callId }); }
    public dispatchQueuedCall(callId: string, targetUsername: string) { socket.emit("admin_queue_dispatch", { callId, targetUsername }); }
    public identifyStaff(username: string, role: string) { if (!username) return; socket.emit("admin_identify", { username, role }); }
    public setStaffStatus(status: string) { socket.emit("staff_set_status", { status }); }
    public forceEndCall(callId: string) { socket.emit("voice_call_end", { callId }); }

    private holdLineInternal(callId: string, isHold: boolean) {
        const line = this.lines.get(callId);
        if (!line) return;
        if (isHold) {
            line.remoteAudioElement.volume = 0;
            if (line.peerConnection && this.sharedLocalStream) {
                line.peerConnection.getSenders().forEach(s => { if (s.track && s.track.kind === 'audio') s.track.enabled = false; });
            }
        } else {
            line.remoteAudioElement.volume = 1.0;
            line.remoteAudioElement.play().catch(() => {});
            if (line.peerConnection && this.sharedLocalStream) {
                line.peerConnection.getSenders().forEach(s => { if (s.track && s.track.kind === 'audio') s.track.enabled = true; });
            }
        }
        this.updateLineSession(callId, { isOnHold: isHold });
    }

    private syncCurrentSessionFromActiveLine() {
        if (this.activeLineId) {
            const line = this.lines.get(this.activeLineId);
            if (line) { this.currentSession = { ...line.session }; this.sessionListeners.forEach(cb => cb(this.currentSession ? { ...this.currentSession } : null)); this.streamListeners.forEach(cb => cb(line.remoteStream)); return; }
        }
        this.currentSession = null;
        this.sessionListeners.forEach(cb => cb(null));
        this.streamListeners.forEach(cb => cb(null));
    }

    private initPeerConnectionForLine(callId: string) {
        const line = this.lines.get(callId);
        if (!line) return;
        line.iceCandidatesQueue = []; line.isRemoteDescriptionSet = false;
        if (line.peerConnection) { try { line.peerConnection.close(); } catch (e) {} }
        line.peerConnection = new RTCPeerConnection(ICE_SERVERS);
        if (this.sharedLocalStream) {
            this.sharedLocalStream.getAudioTracks().forEach(track => {
                const cloned = track.clone();
                if (callId !== this.activeLineId || line.session.isOnHold) cloned.enabled = false;
                line.peerConnection?.addTrack(cloned, this.sharedLocalStream!);
            });
        }
        line.peerConnection.ontrack = (event) => {
            const stream = event.streams?.[0] || new MediaStream([event.track]);
            line.remoteStream = stream;
            line.remoteAudioElement.srcObject = stream;
            line.remoteAudioElement.muted = false;
            line.remoteAudioElement.volume = (callId === this.activeLineId && !line.session.isOnHold) ? 1.0 : 0;
            line.remoteAudioElement.play().catch(() => {});
            if (callId === this.activeLineId) this.streamListeners.forEach(cb => cb(stream));
        };
        line.peerConnection.onicecandidate = (event) => {
            if (event.candidate) socket.emit("voice_call_signal", { callId, signal: { candidate: event.candidate.toJSON ? event.candidate.toJSON() : event.candidate } });
        };
        line.peerConnection.onconnectionstatechange = () => {
            const state = line.peerConnection?.connectionState;
            if (state === "connected") { this.stopRingtone(); this.unlockAudioPlayback(); this.updateLineSession(callId, { status: "connected" }); this.startLineDurationTimer(callId); }
        };
    }

    private async createAndSendOfferForLine(callId: string) {
        const line = this.lines.get(callId);
        if (!line?.peerConnection) return;
        try {
            const offer = await line.peerConnection.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
            await line.peerConnection.setLocalDescription(offer);
            socket.emit("voice_call_signal", { callId, signal: { type: offer.type, sdp: offer.sdp } });
        } catch (e) {}
    }

    private async handleOfferForLine(callId: string, offer: RTCSessionDescriptionInit) {
        const line = this.lines.get(callId);
        if (!line?.peerConnection) return;
        try {
            await line.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
            line.isRemoteDescriptionSet = true;
            await this.flushIceCandidatesQueueForLine(callId);
            const answer = await line.peerConnection.createAnswer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
            await line.peerConnection.setLocalDescription(answer);
            socket.emit("voice_call_signal", { callId, signal: { type: answer.type, sdp: answer.sdp } });
        } catch (e) {}
    }

    private async handleAnswerForLine(callId: string, answer: RTCSessionDescriptionInit) {
        const line = this.lines.get(callId);
        if (!line?.peerConnection) return;
        try { await line.peerConnection.setRemoteDescription(new RTCSessionDescription(answer)); line.isRemoteDescriptionSet = true; await this.flushIceCandidatesQueueForLine(callId); } catch (e) {}
    }

    private async handleIceCandidateForLine(callId: string, candidate: any) {
        const line = this.lines.get(callId);
        if (!line?.peerConnection || !candidate) return;
        try {
            if (!candidate.candidate && candidate.candidate !== "") return;
            const iceCand = new RTCIceCandidate(candidate);
            if (line.isRemoteDescriptionSet && line.peerConnection.remoteDescription) await line.peerConnection.addIceCandidate(iceCand);
            else line.iceCandidatesQueue.push(candidate);
        } catch (e) {}
    }

    private async flushIceCandidatesQueueForLine(callId: string) {
        const line = this.lines.get(callId);
        if (!line?.peerConnection || !line.isRemoteDescriptionSet) return;
        while (line.iceCandidatesQueue.length > 0) {
            const c = line.iceCandidatesQueue.shift();
            if (c) { try { await line.peerConnection.addIceCandidate(new RTCIceCandidate(c)); } catch (e) {} }
        }
    }

    private initPeerConnection() { if (this.activeLineId && this.lines.has(this.activeLineId)) this.initPeerConnectionForLine(this.activeLineId); }
    private async createAndSendOffer() { if (this.activeLineId) await this.createAndSendOfferForLine(this.activeLineId); }
    private async handleOffer(offer: RTCSessionDescriptionInit) { if (this.activeLineId) await this.handleOfferForLine(this.activeLineId, offer); }
    private async handleAnswer(answer: RTCSessionDescriptionInit) { if (this.activeLineId) await this.handleAnswerForLine(this.activeLineId, answer); }
    private async handleIceCandidate(candidate: any) { if (this.activeLineId) await this.handleIceCandidateForLine(this.activeLineId, candidate); }

    private startLineDurationTimer(callId: string) {
        const line = this.lines.get(callId);
        if (!line) return;
        if (line.durationTimer) clearInterval(line.durationTimer);
        const start = line.session.startTime || Date.now();
        line.durationTimer = setInterval(() => {
            const cur = this.lines.get(callId);
            if (cur && cur.session.status === "connected") {
                cur.session.durationSeconds = Math.floor((Date.now() - start) / 1000);
                if (callId === this.activeLineId) { this.currentSession = { ...cur.session }; this.sessionListeners.forEach(cb => cb(this.currentSession ? { ...this.currentSession } : null)); }
            } else { clearInterval(line.durationTimer); line.durationTimer = null; }
        }, 1000);
    }

    private startDurationTimer() { if (this.activeLineId) this.startLineDurationTimer(this.activeLineId); }
    private stopDurationTimer() { for (const line of this.lines.values()) { if (line.durationTimer) { clearInterval(line.durationTimer); line.durationTimer = null; } } }

    private setupAudioAnalyser(stream: MediaStream) {
        try {
            const ctx = this.getOrCreateAudioContext();
            if (ctx) {
                const source = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 64;
                source.connect(analyser);
                const dataArray = new Uint8Array(analyser.frequencyBinCount);
                const checkLevel = () => {
                    analyser.getByteFrequencyData(dataArray);
                    let sum = 0; for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
                    this.audioLevelListeners.forEach(cb => cb(sum / dataArray.length));
                    requestAnimationFrame(checkLevel);
                };
                checkLevel();
            }
        } catch (err) {}
    }

    private playRingtone(isIncoming: boolean) {
        this.stopRingtone();
        try {
            const ctx = this.getOrCreateAudioContext();
            if (!ctx) return;
            const playTone = () => {
                if (!this.audioContext || this.audioContext.state === "closed") return;
                try {
                    const osc = this.audioContext.createOscillator();
                    const gain = this.audioContext.createGain();
                    osc.type = "sine";
                    osc.frequency.setValueAtTime(isIncoming ? 800 : 440, this.audioContext.currentTime);
                    gain.gain.setValueAtTime(0, this.audioContext.currentTime);
                    gain.gain.linearRampToValueAtTime(0.15, this.audioContext.currentTime + 0.05);
                    gain.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + (isIncoming ? 0.6 : 1.2));
                    osc.connect(gain); gain.connect(this.audioContext.destination);
                    osc.start(); osc.stop(this.audioContext.currentTime + (isIncoming ? 0.7 : 1.25));
                } catch (e) {}
            };
            playTone();
            this.ringtoneInterval = setInterval(playTone, isIncoming ? 2000 : 3500);
        } catch (e) {}
    }

    public stopRingtone() { if (this.ringtoneInterval) { clearInterval(this.ringtoneInterval); this.ringtoneInterval = null; } }

    private cleanupLine(callId: string) {
        const line = this.lines.get(callId);
        if (!line) return;
        if (line.durationTimer) { clearInterval(line.durationTimer); line.durationTimer = null; }
        if (line.animationFrameId) { cancelAnimationFrame(line.animationFrameId); line.animationFrameId = null; }
        if (line.peerConnection) { try { line.peerConnection.close(); } catch (e) {} line.peerConnection = null; }
        if (line.remoteAudioElement) { try { line.remoteAudioElement.srcObject = null; line.remoteAudioElement.remove(); } catch (e) {} }
        this.lines.delete(callId);
        if (this.activeLineId === callId) {
            const remaining = Array.from(this.lines.values());
            if (remaining.length > 0) { this.activeLineId = remaining[0].callId; this.syncCurrentSessionFromActiveLine(); }
            else {
                this.activeLineId = null; this.currentSession = null;
                this.sessionListeners.forEach(cb => cb(null)); this.streamListeners.forEach(cb => cb(null));
                if (this.sharedLocalStream) { this.sharedLocalStream.getTracks().forEach(t => { try { t.stop(); } catch (e) {} }); this.sharedLocalStream = null; }
            }
        }
        this.notifyLinesUpdate(); this.persistSessionState(); this.audioLevelListeners.forEach(cb => cb(0));
    }

    public cleanup() {
        this.stopRingtone(); this.stopDurationTimer();
        for (const [callId, line] of this.lines.entries()) {
            if (line.animationFrameId) cancelAnimationFrame(line.animationFrameId);
            if (line.peerConnection) try { line.peerConnection.close(); } catch (e) {}
            if (line.remoteAudioElement) try { line.remoteAudioElement.srcObject = null; line.remoteAudioElement.remove(); } catch (e) {}
            if (line.durationTimer) clearInterval(line.durationTimer);
        }
        this.lines.clear(); this.activeLineId = null;
        if (this.sharedLocalStream) { this.sharedLocalStream.getTracks().forEach(t => { try { t.stop(); } catch (e) {} }); this.sharedLocalStream = null; }
        this.clearPersistedState();
        this.streamListeners.forEach(cb => cb(null)); this.audioLevelListeners.forEach(cb => cb(0)); this.linesListeners.forEach(cb => cb([]));
    }

    public getSession(): VoiceCallSession | null { return this.currentSession; }
    public subscribe(cb: (session: VoiceCallSession | null) => void): () => void { this.sessionListeners.add(cb); cb(this.currentSession ? { ...this.currentSession } : null); return () => this.sessionListeners.delete(cb); }
    public subscribeQueue(cb: (queue: QueuedCallInfo[]) => void): () => void { this.queueListeners.add(cb); return () => this.queueListeners.delete(cb); }
    public subscribeIncomingCalls(cb: (calls: VoiceCallSession[]) => void): () => void {
        const incoming = Array.from(this.lines.values()).filter(l => l.session.status === "incoming").map(l => l.session);
        cb(incoming.length > 0 ? incoming : (this.currentSession && this.currentSession.status === "incoming" ? [this.currentSession] : []));
        return () => {};
    }
    public subscribeRemoteStream(cb: (stream: MediaStream | null) => void): () => void {
        this.streamListeners.add(cb);
        if (this.activeLineId) { const line = this.lines.get(this.activeLineId); if (line?.remoteStream) cb(line.remoteStream); }
        return () => this.streamListeners.delete(cb);
    }
    public subscribeAudioLevel(cb: (level: number) => void): () => void { this.audioLevelListeners.add(cb); return () => this.audioLevelListeners.delete(cb); }
    public subscribeLinesUpdate(cb: (lines: CallLine[]) => void): () => void { this.linesListeners.add(cb); cb(Array.from(this.lines.values())); return () => this.linesListeners.delete(cb); }
}

export const webrtcVoiceService = new WebRtcVoiceService();
